import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { SonaIconComponent } from '@sona/angular-ui-components';
import { BrnTooltipImports } from '@spartan-ng/brain/tooltip';
import { HlmBadgeDirective } from '@spartan-ng/ui-badge-helm';
import { HlmTooltipImports } from '@spartan-ng/ui-tooltip-helm';

type WcagCategoryMapInfo = {
    title: string;
    description: string;
    icon: string;
    prefix: string;
}

const wcagCategoryMap: Record<string, WcagCategoryMapInfo> = {
    perceivable: {
        title: 'REPORTS.COMPONENT.WCAG_CATEGORY_BADGE.PERCEIVABLE.TITLE',
        description: 'REPORTS.COMPONENT.WCAG_CATEGORY_BADGE.PERCEIVABLE.DESCRIPTION',
        icon: 'eye',
        prefix: 'wcag1'
    },
    operable: {
        title: 'REPORTS.COMPONENT.WCAG_CATEGORY_BADGE.OPERABLE.TITLE',
        description: 'REPORTS.COMPONENT.WCAG_CATEGORY_BADGE.OPERABLE.DESCRIPTION',
        icon: 'hand-pointing',
        prefix: 'wcag2'
    },
    understandable: {
        title: 'REPORTS.COMPONENT.WCAG_CATEGORY_BADGE.UNDERSTANDABLE.TITLE',
        description: 'REPORTS.COMPONENT.WCAG_CATEGORY_BADGE.UNDERSTANDABLE.DESCRIPTION',
        icon: 'brain',
        prefix: 'wcag3'
    },
    robust: {
        title: 'REPORTS.COMPONENT.WCAG_CATEGORY_BADGE.ROBUST.TITLE',
        description: 'REPORTS.COMPONENT.WCAG_CATEGORY_BADGE.ROBUST.DESCRIPTION',
        icon: 'devices',
        prefix: 'wcag4'
    }
} as const;

type WcagCategoryMap = typeof wcagCategoryMap;

export type WcagCategory = keyof WcagCategoryMap;

@Component({
    selector: 'sona-wcag-category-cell',
    standalone: true,
    template: `
        @if (_categoryInfo(); as categoryInfo) {
            <hlm-tooltip>
                <span hlmBadge hlmTooltipTrigger size="lg" variant="outline" class="tw-cursor-help">
                    <sona-icon class="tw-mr-2" [name]="categoryInfo.icon" size="small"  appearance="fill" />
                   {{ categoryInfo.title | translate }}
                </span>
                <div *brnTooltipContent class="tw-max-w-80">
                    <div class="tw-font-medium tw-text-lg tw-mb-1">{{ categoryInfo.title | translate }}</div>
                    <div class="tw-text-muted-foreground">{{ categoryInfo.description | translate }}</div>
                </div>
            </hlm-tooltip>
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        HlmBadgeDirective,
        HlmTooltipImports,
        BrnTooltipImports,
        SonaIconComponent,
        TranslatePipe
    ],
    host: {
        class: 'tw-block'
    }
})
export class SonaWcagCategoryCellComponent {
    readonly category = input.required<WcagCategory | null>();
    protected readonly _categoryInfo = computed(() => {
        const category = this.category();
        return category ? wcagCategoryMap[category] : null;
    });
}
